import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Search, Star, Plus, Loader } from 'lucide-react';
import { getUserRecipes, toggleFavorite } from '../utils/recipeService';
import BottomNav from '../components/BottomNav';

export default function RecipeLibrary() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, favorites
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    loadRecipes();
  }, [currentUser]);

  async function loadRecipes() {
    try {
      setLoading(true);
      const userRecipes = await getUserRecipes(currentUser.uid);
      setRecipes(userRecipes);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleFavorite(recipeId, currentStatus) {
    try {
      await toggleFavorite(recipeId, currentStatus);
      // Update local state
      setRecipes(recipes.map(r => 
        r.id === recipeId ? { ...r, isFavorite: !currentStatus } : r
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }

  // Filter recipes
  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || (filter === 'favorites' && recipe.isFavorite);
    return matchesSearch && matchesFilter;
  });

  const favoriteCount = recipes.filter(r => r.isFavorite).length;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header - NOT STICKY */}
      <div className="relative h-64 overflow-hidden mb-6">
        {/* Purple Blob */}
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-60px',
            left: '-60px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        {/* Pink Blob */}
        <div 
          className="absolute w-64 h-64 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '60px',
            right: '-80px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        {/* Yellow Blob */}
        <div 
          className="absolute w-56 h-56 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-40px',
            left: '90px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        {/* Header Content */}
        <div className="relative z-10 max-w-md mx-auto px-6 pt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/')} 
                className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
                style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
              <h1 
                className="text-white text-3xl logo"
                style={{
                  textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)',
                }}
              >
                My Recipes
              </h1>
            </div>
            <button 
              onClick={() => navigate('/upload')}
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <Plus size={20} className="text-white" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search size={18} className="absolute left-4 top-4 text-purple-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search recipes..."
              className="w-full pl-11 pr-4 py-3.5 bg-white/95 backdrop-blur-sm text-gray-800 rounded-2xl focus:outline-none focus:ring-2 focus:ring-white/50 placeholder-gray-500"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            />
          </div>
        </div>
      </div>

      {/* Content - Added padding top for spacing */}
      <div className="max-w-md mx-auto px-6 pt-6">
        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl p-2 mb-6 shadow-md flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all ${
              filter === 'all'
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            All ({recipes.length})
          </button>
          <button
            onClick={() => setFilter('favorites')}
            className={`flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all ${
              filter === 'favorites'
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            ⭐ Favorites ({favoriteCount})
          </button>
        </div>

        {/* Recipes Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={40} className="animate-spin text-purple-600" />
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 text-center shadow-md">
            <div className="text-6xl mb-4">
              {searchTerm ? '🔍' : filter === 'favorites' ? '⭐' : '📚'}
            </div>
            <h3 className="font-bold text-gray-800 mb-2">
              {searchTerm 
                ? 'No recipes found' 
                : filter === 'favorites' 
                  ? 'No favorites yet'
                  : 'No recipes yet'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchTerm
                ? 'Try a different search term'
                : filter === 'favorites'
                  ? 'Star recipes to add them to favorites'
                  : 'Start by uploading your first recipe!'}
            </p>
            {!searchTerm && filter === 'all' && (
              <button
                onClick={() => navigate('/upload')}
                className="text-purple-600 font-semibold text-sm hover:underline"
              >
                Upload a recipe →
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4 pb-6">
            {filteredRecipes.map((recipe) => (
              <div
                key={recipe.id}
                onClick={() => navigate(`/recipe/${recipe.id}`)}
                className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1 border border-gray-100"
              >
                {/* Recipe Image/Icon */}
                <div 
                  className="h-36 flex items-center justify-center text-6xl relative"
                  style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #fce7f3 100%)' }}
                >
                  🍽️
                  {/* Favorite Star */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(recipe.id, recipe.isFavorite);
                    }}
                    className="absolute top-3 right-3 bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all"
                  >
                    <Star
                      size={20}
                      className={recipe.isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}
                    />
                  </button>
                </div>

                {/* Recipe Info */}
                <div className="p-5">
                  <h3 className="font-bold text-gray-800 mb-2 text-lg">{recipe.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {recipe.ingredients?.length || 0} ingredients
                    {recipe.source && ` • ${recipe.source}`}
                  </p>
                  {recipe.tags && recipe.tags.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {recipe.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag-pink">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
